import Cookies from 'js-cookie';
import { Message } from 'byted-vui';
import './index-red.css';
import { OauthOptions, IOptions } from '@ad/authorization-i18n-sdk';
import {
  FullNormalFormData,
  AcoFormData,
  IDENTITY_TYPE_MAP,
  IDENTITY_AVAILABLE_STATUS,
  IdentityInfo,
  JUDGE_IDENTITY_PERMISSION_LIST,
  AdgroupFormData,
  CampaignFormData,
  NormalCreative,
  HIDE_TTBA_ACCOUNT_ROLE_LIST,
  SHOW_TTBA_COUNTRY_LIST,
  IdentityItem,
  isBOE,
  UNAVAILABLE_IDENTITY_ACCOUNT_STATUS_MAP,
  NATIVE_AD_ACTION_TYPE,
  AvatarIcon,
  ShopType,
} from '@tt4b/ttam-enums';
import {
  ValidationError,
  useAddIdentityApi,
  useAuthorizeTTVideoApi,
  useDoubleTTAuthorizeApi,
  useIdentityListApi,
  useRequest,
  useTTVideoInfoApi,
  useIdentityLink,
  useStdAdgroupIs,
  useCreativeAdgroupIs,
  useStore,
  StateEnum,
  useCreativeActionTypeIs,
  useStdCreativeAdgroupIs,
  useIdentityInfoApi,
  fetchUpdateIdentity,
  useIdentityGet,
  useIdentityAdditional,
  useStdCampaignIs,
  useAllowlist,
  useAffiliateVideoInfoApi,
} from '@tt4b/ttam-hooks';
import { computed, ref, Ref, onMounted, h, watchEffect, watch } from '@vue/composition-api';
import { i18n } from './../../i18n';
import {
  CreateIdentityFormData,
  IdentityFullInfo,
  PostAuthorizeForm,
  TikTokAccountType,
  TypeSelectItem,
  NativeIdentityType,
} from './types';
import TiktokSVG from '@tt4b/ttam-assets/src/svgs/creative/use-account-icon-ttba.svg?url';
import PostSVG from '@tt4b/ttam-assets/src/svgs/creative/use-account-icon-ttca.svg?url';
import { IDENTITY_DISABLED_SWITCH_MAP, IDENTITY_TYPE_CONFIG, SHOW_IDENTITY_AVAILABLE_LIST } from './const';
import {
  getAdSourceByNormalCreativeData,
  getImgSrcByEnv,
  isNativeAdByItemSource,
  isNativeAdIdentityType,
  parseUrlSearch,
  sleepFn,
  stringLength,
  trackEvent,
  imgSrcPrefix,
  logger,
} from '@tt4b/ttam-utils';
import defaultAvatar from '@tt4b/ttam-assets/src/images/creative/default-avatar.png';
import { createSharedComposable } from '@vueuse/core';

const MAX_APP_NAME_LENGTH = 40;
export const BCDomain = isBOE ? '//boei18n-business.byteoversea.net' : '//business.tiktok.com';
export function handelIdentityItem(
  info: IdentityInfo,
  opt?: {
    isStandardAds?: boolean;
    isShopLive?: boolean;
    isDisabledTTCodeUser?: boolean;
    isProductFromShowcase?: boolean;
  },
) {
  const { isStandardAds, isShopLive, isDisabledTTCodeUser, isProductFromShowcase } = opt ?? {};
  const authCodeUserShowPopover = isStandardAds || isShopLive;
  const { AUTH_CODE_USER, TT_USER, BC_AUTH_TT, TTS_TT } = IDENTITY_TYPE_MAP;
  const availableStatus = info.availableStatus === IDENTITY_AVAILABLE_STATUS.Available;
  // shop 的 live video 需要额外判断 available
  let isAvailable = availableStatus;
  if (isProductFromShowcase) {
    isAvailable = info.canUseShowcase;
  } else if (isShopLive) {
    isAvailable = info.canUseLiveList;
  } else if (isStandardAds) {
    isAvailable = info.canUseVideoList;
  }

  const item: IdentityItem = {
    displayName: info?.displayName,
    id: info?.identityId,
    avatar: (info?.avatarIcon?.url as string) ?? '',
    enablePopover: !isAvailable,
    disabled: !isAvailable,
    desc: (IDENTITY_TYPE_CONFIG[info.identityType]?.[isAvailable ? 'name' : 'unavailableName'] as string) ?? '',
    descIcon: IDENTITY_TYPE_CONFIG[info.identityType]?.[isAvailable ? 'icon' : 'disabledIcon'] ?? '',
  };

  // TikTok 账号
  if (info.identityType === TT_USER) {
    item.disabled = !isAvailable;
    item.enablePopover = !isAvailable;
    item.popoverTitle =
      (UNAVAILABLE_IDENTITY_ACCOUNT_STATUS_MAP()?.[info.availableStatus]?.tooltipText as string) ?? '';
    item.popoverCTA =
      (UNAVAILABLE_IDENTITY_ACCOUNT_STATUS_MAP()?.[info.availableStatus]?.authorizationDesc as string) ?? '';
  }

  // 视频授权场景 shop 场景，需要持续展示 popover 文案
  if (info.identityType === AUTH_CODE_USER && authCodeUserShowPopover) {
    item.enablePopover = true;
    if (isDisabledTTCodeUser) {
      item.disabled = true;
      item.descIcon = IDENTITY_TYPE_CONFIG[AUTH_CODE_USER]?.disabledIcon;
      item.popoverTitle = i18n.t('module_ttba_push_video_native_ad_forbid_code_tt_user_introduce') as string;
    } else {
      item.disabled = false;
      item.popoverTitle = i18n.t('module_creative_authorized_tiktok_account_introduce') as string;
    }
  }

  // BC 场景
  if (info.identityType === BC_AUTH_TT) {
    const popoverStatus = isShopLive
      ? IDENTITY_AVAILABLE_STATUS.BC_NO_LIVE_RIGHT
      : IDENTITY_AVAILABLE_STATUS.BC_NO_VIDEO_RIGHT;
    item.popoverTitle = UNAVAILABLE_IDENTITY_ACCOUNT_STATUS_MAP()?.[popoverStatus].tooltipText as string;
    item.popoverCTA = UNAVAILABLE_IDENTITY_ACCOUNT_STATUS_MAP()?.[popoverStatus].authorizationDesc as string;
    item.popoverCTALink = `${BCDomain}/manage/tiktok?org_id=${info.identityBcId}`;
  }
  // TTS official account场景
  if (info.identityType === TTS_TT) {
    const popoverStatus = isShopLive
      ? IDENTITY_AVAILABLE_STATUS.TTS_NO_LIVE_RIGHT
      : IDENTITY_AVAILABLE_STATUS.TTS_NO_VIDEO_RIGHT;
    item.popoverTitle = UNAVAILABLE_IDENTITY_ACCOUNT_STATUS_MAP()?.[popoverStatus].tooltipText as string;
    item.popoverCTA = UNAVAILABLE_IDENTITY_ACCOUNT_STATUS_MAP()?.[popoverStatus].authorizationDesc as string;
    item.popoverTea = {
      'data-tea-expose': isShopLive ? 'lsa_tt_invalid_permission' : 'vsa_tt_invalid_permission',
    };
    item.popoverCtaTea = isShopLive ? 'lsa_tt_learn_more' : 'vsa_tt_learn_more';
    item.popoverCTALink = '/help/article/how-to-get-showcase-permission';
  }

  if (isShopLive && info?.allowCreateRoi1Ad === false) {
    item.enablePopover = true;
    item.disabled = true;
    item.popoverTitle = i18n.t('module_adgroup_identity_roi2_mutex') as string;
    item.popoverCTA = '';
  }

  return {
    ...item,
    ...info,
  };
}

export function useTiktokIdentityVisible(opt: {
  creativeData: Ref<Partial<FullNormalFormData & AcoFormData>>;
  createMode: Ref<string>;
  adgroupData: Ref<AdgroupFormData>;
  bindTtbaAccountData: Ref<Partial<IdentityInfo>>;
}) {
  const { state } = useStore();
  const accountInfo = computed(() => state[StateEnum.accountInfo].data.value);
  const { creativeData, createMode, adgroupData, bindTtbaAccountData } = opt;
  const { isCreate } = useCreativeActionTypeIs({
    createMode,
    adgroupFormData: adgroupData,
  });
  const isHideTTBAAccount = computed(
    () =>
      HIDE_TTBA_ACCOUNT_ROLE_LIST.includes(accountInfo.value?.account?.customer_type_value) &&
      !SHOW_TTBA_COUNTRY_LIST.includes(accountInfo.value?.account?.country),
  );

  const visible = computed(() => {
    if (!isCreate.value && creativeData.value?.identityType === IDENTITY_TYPE_MAP.TT_USER) {
      return true;
    }

    return !(isHideTTBAAccount.value ? !bindTtbaAccountData.value?.identityId : false);
  });

  return { visible };
}

export function useIdentityTypeTipsPropsTipsAndLink() {
  const LEARN_MORE_LINK_MAP = {
    TTBA: '/help/article?aid=10001865',
    TTA: '/help/article?aid=10001881',
  };

  const tips = computed(() => i18n.t('module_creative_identity_native_tab_ttac_desc'));

  const linkUrl = computed(() => LEARN_MORE_LINK_MAP.TTBA);
  const linkText = ref(i18n.t('common_learn_more'));

  return { tips, linkUrl, linkText };
}

export function useIdentityTypeProps(opt: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
  creativeData: Ref<Partial<FullNormalFormData & AcoFormData>>;
  createMode: Ref<string>;
  isDisabledCreative: Ref<boolean>;
}) {
  const {
    DISABLED_NOT_SUPPORT_ACO,
    DISABLED_NATIVE_TO_NORMAL,
    DISABLED_NORMAL_TO_NATIVE,
    DISABLED_NOT_CONTAIN_TT_POSITION,
    DISABLED_OBJECTIVE_NOT_SUPPORT_NATIVE,
    DISABLED_OBJECTIVE_NOT_SUPPORT_NORMAL,
  } = IDENTITY_DISABLED_SWITCH_MAP;
  const { campaignData, adgroupData, creativeData, createMode, isDisabledCreative } = opt;
  const { isEdit } = useCreativeActionTypeIs({
    createMode,
    adgroupFormData: adgroupData,
  });
  const { isHasTikTok, isOnlyPangle } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const { isSupportNativeAd, isSupportNormalAd, isAco } = useStdCreativeAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const { isCreateInExistAcoAdgroup, isStandardAdsWithShop, isCLA } = useCreativeAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
    createMode,
  });

  const isNative = computed(() => {
    if (isAco.value) {
      return (
        isSupportNativeAd.value &&
        isNativeAdIdentityType(creativeData.value.identityType || IDENTITY_TYPE_MAP.FAKE_USER)
      );
    }
    return (
      isNativeAdByItemSource(getAdSourceByNormalCreativeData(creativeData.value as NormalCreative)) ||
      isNativeAdIdentityType(creativeData.value.identityType)
    );
  });

  const disabled = computed(
    () =>
      isDisabledCreative.value ||
      isEdit.value ||
      isCreateInExistAcoAdgroup.value ||
      !isSupportNativeAd.value ||
      !isSupportNormalAd.value,
  );

  const disabledTips = computed(() => {
    if (isEdit.value) {
      // return isNativeAdIdentityType(creativeData.value.identityType)
      //   ? DISABLED_NORMAL_TO_NATIVE
      //   : DISABLED_NATIVE_TO_NORMAL;
    } else {
      if (!isHasTikTok.value) {
        return DISABLED_NOT_CONTAIN_TT_POSITION;
      } else if (!isSupportNativeAd.value) {
        return isAco.value ? DISABLED_NOT_SUPPORT_ACO : DISABLED_OBJECTIVE_NOT_SUPPORT_NATIVE;
      } else if (isAco.value && isSupportNativeAd.value) {
        return isNative.value ? DISABLED_NATIVE_TO_NORMAL : DISABLED_NORMAL_TO_NATIVE;
      } else if (!isSupportNormalAd.value) {
        return DISABLED_OBJECTIVE_NOT_SUPPORT_NORMAL;
      }
    }
    return '';
  });

  const visible = computed(() => !(isStandardAdsWithShop.value || isCLA.value || isOnlyPangle.value));

  return { disabled, disabledTips, visible };
}

export function useCustomCreateAvatarProps() {
  const { state } = useStore();
  const aadvid = computed(() => state[StateEnum.baseInfo].value.aadvid);
  const headers = {
    'X-CSRFToken': Cookies.get('csrftoken'),
  };
  const action = `/api/v3/i18n/tools/upload_image/?aadvid=${aadvid.value}`;

  return {
    headers,
    action,
  };
}

export function useCustomCreateSubmit(opt: { modalForm: Ref<CreateIdentityFormData> }) {
  const { modalForm } = opt;
  const { updateIdentityCacheList } = useIdentityGet();
  const params = computed(() => ({
    displayName: modalForm.value?.displayName ?? '',
    profileImage: modalForm.value?.avatarIcon?.webUri ?? '',
    identityType: IDENTITY_TYPE_MAP.FAKE_USER,
  }));
  const { run: _run, data, isLoading: _loading, error } = useRequest(useAddIdentityApi, params, { manual: true });
  const isSleeping = ref(false);
  const run = async () => {
    isSleeping.value = true;
    await _run();
    updateIdentityCacheList('add', data.value.identityInfo);
    isSleeping.value = false;
  };

  const isLoading = computed(() => _loading.value || isSleeping.value);

  return {
    run,
    data,
    isLoading,
    error,
  };
}

export function useCustomUpdateSubmit(opt: {
  modalForm: Ref<CreateIdentityFormData>;
  originData: Ref<Partial<IdentityFullInfo>>;
}) {
  const { modalForm, originData } = opt;
  const { updateIdentityCacheList } = useIdentityGet();
  const params = computed(() => ({
    identityId: originData.value?.identityId || '',
    displayName: modalForm.value?.displayName,
    profileImage: (modalForm.value?.avatarIcon?.webUri || '') as string,
    identityType: IDENTITY_TYPE_MAP.FAKE_USER,
  }));
  const { run: _run, data, isLoading: _loading, error } = useRequest(fetchUpdateIdentity, params, { manual: true });
  const isSleeping = ref(false);
  const run = async () => {
    isSleeping.value = true;
    await _run();
    updateIdentityCacheList('edit', {
      oldIdentityId: params.value.identityId,
      newIdentityInfo: data.value.identityInfo,
    });
    isSleeping.value = false;
  };

  const isLoading = computed(() => _loading.value || isSleeping.value);

  return {
    run,
    data,
    isLoading,
    error,
  };
}

export const useCustomCreateRules = createSharedComposable(function useCustomCreateRules() {
  const isLenValid = (val: string) => {
    const len = stringLength(`${val ?? ''}`.trim());
    if (len < 1 || len > MAX_APP_NAME_LENGTH) {
      return false;
    } else {
      return true;
    }
  };
  const displayNameInnerValidator = computed(() => [
    {
      validator: (rule, value, callback) => {
        if (!isLenValid(value)) {
          return callback(new Error(i18n.t('adgroup_display_name_hint') as string));
        }
        return callback();
      },
      trigger: 'blur',
    },
  ]);
  const displayNameValidator = computed(() => [
    {
      validator: (rule, value, callback, data) => {
        const displayName = data?.identityData?.displayName || value;
        if (!isLenValid(displayName)) {
          return callback(new Error(i18n.t('adgroup_display_name_hint') as string));
        }
        return callback();
      },
      trigger: 'blur',
    },
  ]);

  return {
    displayNameInnerValidator,
    displayNameValidator,
  };
});

export function useCustomListDataSource(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData, campaignData } = opt;
  const { getIdentityListByIdentityType } = useIdentityGet();
  const { isLiveAds, isStandardAds } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const isLoading = ref(false);
  const error = ref();
  const { identityAdditionalInfo } = useIdentityAdditional();
  const run = async () => {
    isLoading.value = true;
    await getIdentityListByIdentityType([IDENTITY_TYPE_MAP.FAKE_USER]).catch(e => {
      error.value = e;
    });
    isLoading.value = false;
  };

  const originalData = computed(() => identityAdditionalInfo.value.list);
  const dataSource = computed<Array<IdentityInfo & IdentityItem>>(() =>
    originalData.value?.map(i =>
      handelIdentityItem(i, {
        isShopLive: isLiveAds.value,
        isStandardAds: isStandardAds.value,
      }),
    ),
  );
  const isEmpty = computed(() => dataSource.value?.length === 0);

  onMounted(() => {
    run();
  });

  return {
    run,
    error,
    isEmpty,
    isLoading,
    dataSource,
    originalData,
  };
}

export function useTikTokListDataSource(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData, campaignData } = opt;
  const { isLiveAds, isStandardAds } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { BC_AUTH_TT, AUTH_CODE_USER, TTS_TT } = IDENTITY_TYPE_MAP;
  const { isProductSalesCloseLoop } = useStdCampaignIs({ campaignFormData: campaignData });
  const { isTtsTTWhite } = useAllowlist();
  const identityTypeList = computed(() => {
    const list = [BC_AUTH_TT, AUTH_CODE_USER];
    if (isProductSalesCloseLoop.value && isTtsTTWhite.value && adgroupData.value.shopType !== ShopType.tt4bShop) {
      list.push(TTS_TT);
    }
    return list;
  });

  const { run, data, isLoading, error } = useRequest(
    useIdentityListApi,
    {
      identityTypeList: identityTypeList.value,
    },
    { manual: true },
  );

  onMounted(() => {
    run();
  });

  const originalData = computed(() => {
    // 如果tts tt所属的shop，不是当前表单所选定的shop，则要过滤掉这个tts tt账号
    const result = !identityTypeList.value.includes(IDENTITY_TYPE_MAP.TTS_TT)
      ? data.value?.identityList
      : data.value?.identityList.filter(
          item =>
            !(item.identityType === IDENTITY_TYPE_MAP.TTS_TT && item.identityTtsShopId !== adgroupData.value?.shopId),
        );
    return result || [];
  });

  const dataSource = computed<Array<IdentityInfo & IdentityItem>>(() =>
    originalData.value?.map(i =>
      handelIdentityItem(i, {
        isShopLive: isLiveAds.value,
        isStandardAds: isStandardAds.value,
      }),
    ),
  );

  const isEmpty = computed(() => dataSource.value?.length === 0);

  return {
    run,
    error,
    isEmpty,
    isLoading,
    dataSource,
    originalData,
  };
}
export function useAffiliateTikTokListDataSource(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { getStore } = useStore();
  const { adgroupData, campaignData } = opt;
  const { isLiveAds, isStandardAds } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { BC_AUTH_TT, AUTH_CODE_USER, TTS_TT, TT_USER } = IDENTITY_TYPE_MAP;
  const identityTypeList = [BC_AUTH_TT, AUTH_CODE_USER, TT_USER, TTS_TT];
  const { data, isLoading, isEmpty, runningPromise, run } = getStore(
    StateEnum.identityList,
    {
      identityTypeList,
      needBcInfo: true,
    },
    false,
    false,
  );

  const originalData = computed(() => {
    // 如果tts tt所属的shop，不是当前表单所选定的shop，则要过滤掉这个tts tt账号
    const result = data.value.identityList.filter(
      item => !(item.identityType === IDENTITY_TYPE_MAP.TTS_TT && item.identityTtsShopId !== adgroupData.value?.shopId),
    );
    return result || [];
  });

  const dataSource = computed<Array<IdentityInfo & IdentityItem>>(() =>
    originalData.value?.map(i =>
      handelIdentityItem(i, {
        isShopLive: isLiveAds.value,
        isStandardAds: isStandardAds.value,
      }),
    ),
  );
  const tikTokAccountOriginalData = computed(() =>
    originalData.value.find(item => item.identityType === IDENTITY_TYPE_MAP.TT_USER),
  );
  const getAffilidateIdentityList = () =>
    run({
      identityTypeList,
      needBcInfo: true,
    });
  const hasTTBA = computed(() =>
    Boolean(dataSource.value.filter(identity => identity.identityType === TT_USER).length),
  );
  const ttbaIdentity = computed(() => dataSource.value.find(identity => identity.identityType === TT_USER));
  return {
    run,
    isEmpty,
    hasTTBA,
    ttbaIdentity,
    isLoading,
    dataSource,
    originalData,
    tikTokAccountOriginalData,
    runningPromise,
    getAffilidateIdentityList,
  };
}
export function usePostAuthorizeRules() {
  const videoCodeValidator = computed(() => [
    {
      validator: (rule, value, callback) => {
        const len = stringLength(`${value}`.trim());

        if (len === 0) {
          return callback(new Error(i18n.t('module_library_native_video_code_empty') as string));
        }

        return callback();
      },
      trigger: 'blur',
    },
  ]);
  const fromVideoCodeValidator = computed(() => [
    {
      validator: (rule, value, callback) => {
        const len = stringLength(`${value}`.trim());

        if (len === 0) {
          return callback(new Error(i18n.t('module_library_native_video_original_post_code_empty') as string));
        }

        return callback();
      },
      trigger: 'blur',
    },
  ]);

  return {
    videoCodeValidator,
    fromVideoCodeValidator,
  };
}

export function usePostAuthorizeDataSource(opt: { params: Ref<PostAuthorizeForm> }) {
  const { data, run, isLoading, error, reset } = useRequest(useTTVideoInfoApi, opt.params, { manual: true });

  return {
    data,
    error,
    isLoading,
    run,
    reset,
  };
}

export function usePostAuthorizeDoubleDataSource(opt: { params: Ref<PostAuthorizeForm> }) {
  const params = computed(() => ({
    ...opt.params.value,
    onlyValidate: true,
  }));
  const { run, data, isLoading, error, reset } = useRequest(useDoubleTTAuthorizeApi, params, { manual: true });

  return {
    data,
    isLoading,
    error,
    run,
    reset,
  };
}

export function usePostAuthorizeSubmit(opt: { params: Ref<PostAuthorizeForm>; needDoubleAuthorize: Ref<boolean> }) {
  const { params, needDoubleAuthorize } = opt;
  const authorizeParams = computed(() => ({ videoCode: params.value.videoCode }));
  const doubleAuthorizeParams = computed(() => ({
    videoCode: params.value.videoCode,
    fromVideoCode: params.value.fromVideoCode,
    onlyValidate: false,
  }));
  const { run: authorize, isLoading: authorizeLoading } = useRequest(useAuthorizeTTVideoApi, authorizeParams, {
    manual: true,
  });
  const { run: doubleAuthorize, isLoading: doubleAuthorizeLoading } = useRequest(
    useDoubleTTAuthorizeApi,
    doubleAuthorizeParams,
    { manual: true },
  );

  const submit = () => (needDoubleAuthorize.value ? doubleAuthorize() : authorize());
  const isLoading = computed(() => (needDoubleAuthorize.value ? doubleAuthorizeLoading.value : authorizeLoading.value));

  return { submit, isLoading };
}

export function useTikTokTypeSelectPropsMap(opt: { campaignData: Ref<CampaignFormData> }) {
  const { campaignData } = opt;
  const { isProductSalesCloseLoop } = useStdCampaignIs({ campaignFormData: campaignData });

  const map = computed<Record<NativeIdentityType, TypeSelectItem>>(() => ({
    tiktok: {
      title: i18n.t('module_creative_bc_deliver_through_ba') as string,
      desc: i18n.t('module_creative_bc_deliver_through_ba_prompt') as string,
      icon: TiktokSVG,
      value: [IDENTITY_TYPE_MAP.TT_USER],
    },
    post: {
      title: i18n.t('module_creative_bc_utilize_tt_creator_account') as string,
      desc: i18n.t('module_creative_bc_utilize_tt_creator_account_prompt') as string,
      icon: PostSVG,
      value: [IDENTITY_TYPE_MAP.BC_AUTH_TT, IDENTITY_TYPE_MAP.AUTH_CODE_USER].concat(
        isProductSalesCloseLoop.value ? [IDENTITY_TYPE_MAP.TTS_TT] : [],
      ),
    },
  }));

  return { map };
}

export function useTikTokAccountDataSource() {
  const { TT_USER } = IDENTITY_TYPE_MAP;
  const { run, data, isLoading, error } = useRequest(
    useIdentityListApi,
    { identityTypeList: [TT_USER] },
    { manual: true },
  );
  const originalData = computed(() => data.value?.identityList?.[0] || {});

  onMounted(() => {
    run();
  });

  const dataSource = computed<TikTokAccountType>(() => {
    const item = data.value?.identityList?.[0];
    return {
      name: item?.displayName,
      id: item?.identityId,
      avatar: item?.avatarIcon?.url as string,
      unavailable: SHOW_IDENTITY_AVAILABLE_LIST.includes(item?.availableStatus),
      availableStatus: item?.availableStatus,
    };
  });
  const isEmpty = computed(() => data.value?.identityList?.length === 0);

  return {
    run,
    error,
    isLoading,
    dataSource,
    originalData,
    isEmpty,
  };
}

export function useTikTokAccountEvents<T = any>(opt: {
  oauthConfig?: Ref<OauthOptions>;
  updateTTBAConfig?: Ref<IOptions>;

  onUnLinkSuccess?: () => void;
  onLinkSuccess?: (data: T) => void;
  onLinkFail?: (data: T) => void;
}) {
  const { oauthConfig, onLinkSuccess, onLinkFail, onUnLinkSuccess, updateTTBAConfig } = opt;
  const onUpdateTTBASuccess = data => {
    const { desc = '', action = {} } = data;
    const { text = '', url = '' } = action;
    Message({
      message: h('p', [
        h('span', desc),
        h(
          'a',
          {
            style: 'color: #F60457;padding-left:8px;text-decoration: none;margin-right: 20px;',
            attrs: { href: url, target: '_blank' },
          },
          text,
        ),
      ]),
      type: 'success',
      showClose: true,
      duration: 3000,
    });
  };
  const {
    onLink,
    onUnLink,
    updateTTBA: selfUpdateTTBA,
  } = useIdentityLink({
    oauthConfig,
    updateTTBAConfig,
    onLinkFail,
    onLinkSuccess,
    onUnLinkSuccess,
  });

  const updateTTBA = async (domId: string) => {
    try {
      const data = await selfUpdateTTBA({ domId });
      onUpdateTTBASuccess(data);
      trackEvent('upgrade_identity_upgrade_ba_success');
    } catch (err) {
      trackEvent('upgrade_identity_upgrade_ba_failed', {
        upgrade_tt_failed_reason: err,
      });
    }
  };

  return {
    onLink,
    onUnLink,
    updateTTBA,
  };
}

export function useTikTokTipsProps(opt: {
  createMode?: Ref<string>;
  identityInfo?: Ref<IdentityInfo>;
  adgroupData: Ref<AdgroupFormData>;
}) {
  const { createMode, identityInfo, adgroupData } = opt;
  const { isEdit } = useCreativeActionTypeIs({
    adgroupFormData: adgroupData,
    createMode,
  });
  const getStatus = (info: IdentityInfo) => {
    const { identityType, availableStatus } = info || {};
    // tts tt，权限不足(available_status=2)时，需要改写状态码，使用不同的提示语、cta
    return identityType === IDENTITY_TYPE_MAP.TTS_TT && availableStatus === IDENTITY_AVAILABLE_STATUS.SCOPE_UNAVAILABLE
      ? IDENTITY_AVAILABLE_STATUS.TTS_SCOPE_UNAVAILABLE
      : availableStatus;
  };
  const title = computed(() => {
    const status = getStatus(identityInfo?.value);
    return isEdit?.value
      ? UNAVAILABLE_IDENTITY_ACCOUNT_STATUS_MAP()?.[status]?.editUnAvailableDesc
      : UNAVAILABLE_IDENTITY_ACCOUNT_STATUS_MAP()?.[status]?.tooltipText ?? '';
  });

  const ctaText = computed(() => {
    const status = getStatus(identityInfo?.value);
    return isEdit?.value
      ? UNAVAILABLE_IDENTITY_ACCOUNT_STATUS_MAP()?.[status]?.reAuthorizationDesc
      : UNAVAILABLE_IDENTITY_ACCOUNT_STATUS_MAP()?.[status]?.authorizationDesc ?? '';
  });

  return { title, ctaText };
}

export function useTikTokTipsVisible(opt: { identityInfo?: Ref<IdentityInfo> }) {
  const { identityInfo } = opt;
  const isUnAvailable = computed(() => identityInfo?.value?.availableStatus !== IDENTITY_AVAILABLE_STATUS.Available);
  const isUnAvailableIdentityAccount = computed(
    () => JUDGE_IDENTITY_PERMISSION_LIST.includes(identityInfo?.value?.identityType) && isUnAvailable.value,
  );

  return {
    visible: isUnAvailableIdentityAccount,
  };
}

// isDisabledTTAccount
// isDisabledTTAuthCodeAccount
// isDisabledTTAccount

export function useSaleTikTokListDataSource(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData, campaignData } = opt;
  const { isLiveAds, isStandardAds, isProductSales } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { isProductSalesCloseLoop } = useStdCampaignIs({ campaignFormData: campaignData });
  const { isTtsTTWhite, isShopMultipleAttrWhite } = useAllowlist();
  // 混场归因白名单下，LSA拉取identity，不要拉取tts tt，不支持
  const isLsaMultiAttr = isShopMultipleAttrWhite.value && isLiveAds.value;
  const { TT_USER, BC_AUTH_TT, AUTH_CODE_USER, TTS_TT } = IDENTITY_TYPE_MAP;
  const params = computed(() => {
    const identityTypeList = [TT_USER, BC_AUTH_TT];
    if (isStandardAds.value) {
      identityTypeList.push(AUTH_CODE_USER);
    }
    if (
      isProductSalesCloseLoop.value &&
      isTtsTTWhite.value &&
      adgroupData.value.shopType !== ShopType.tt4bShop &&
      !isLsaMultiAttr
    ) {
      identityTypeList.push(TTS_TT);
    }
    const isNeedMutex = isProductSales.value && isLiveAds.value;
    return {
      identityTypeList,
      needAllowRoi1Action: isNeedMutex,
    };
  });
  const { run, data, isLoading, error } = useRequest(useIdentityListApi, params, { manual: true });

  const originalData = computed(() => {
    // 如果拉取的类型中无tts tt || shop都还没有选，则直接返回list，无需过滤
    const result =
      !params.value.identityTypeList.includes(IDENTITY_TYPE_MAP.TTS_TT) || !adgroupData.value?.shopId
        ? data.value?.identityList
        : data.value?.identityList.filter(
            item =>
              // 如果tts tt所属的shop，不是当前表单所选定的shop，则要过滤掉这个tts tt账号
              !(item.identityType === IDENTITY_TYPE_MAP.TTS_TT && item.identityTtsShopId !== adgroupData.value?.shopId),
          );
    return result || [];
  });
  const isEmpty = computed(() => originalData.value?.length === 0);

  const dataSource = computed<IdentityItem[]>(() =>
    originalData.value?.map(item =>
      handelIdentityItem(item, { isShopLive: isLiveAds.value, isStandardAds: isStandardAds.value }),
    ),
  );
  const hasLoaded = ref(false);
  onMounted(() => {
    run().then(() => {
      hasLoaded.value = true;
    });
  });

  return {
    run,
    error,
    isLoading,
    dataSource,
    originalData,
    isEmpty,
    typeListParams: params,
    hasLoaded,
  };
}

export function useShopVideoTikTokList(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData, campaignData } = opt;
  const { TT_USER, BC_AUTH_TT, AUTH_CODE_USER } = IDENTITY_TYPE_MAP;
  const { isLiveAds, isStandardAds } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { run, data, isLoading, error } = useRequest(
    useIdentityListApi,
    { identityTypeList: [TT_USER, BC_AUTH_TT, AUTH_CODE_USER] },
    { manual: true },
  );
  const originalData = computed(() => data.value?.identityList ?? []);

  const dataSource = computed<IdentityItem[]>(() =>
    data.value?.identityList?.map(item =>
      handelIdentityItem(item, {
        isShopLive: isLiveAds.value,
        isStandardAds: isStandardAds.value,
      }),
    ),
  );

  const isEmpty = computed(() => dataSource.value?.length === 0);

  return {
    run,
    error,
    isLoading,
    dataSource,
    originalData,
    isEmpty,
  };
}

export function useIdentityRules(option: { createMode: Ref<string>; adgroupData: Ref<AdgroupFormData> }) {
  const identityValidator = computed(() => [
    {
      validator: (rule, value, callback, data) => {
        logger.info('useIdentityRules validator parameters: value=%s, data=%s', value, JSON.stringify(data));
        const { identityType, identityData, identityId } = data;
        const { availableStatus = 0 } = identityData || {};
        const { createMode, adgroupData } = option;
        const { isEdit } = useCreativeActionTypeIs({
          createMode,
          adgroupFormData: adgroupData,
        });
        // identity 权限校验
        const isPermissionsError =
          isEdit.value &&
          JUDGE_IDENTITY_PERMISSION_LIST.includes(identityType || 0) &&
          availableStatus !== IDENTITY_AVAILABLE_STATUS.Available;
        if (isPermissionsError) {
          // TTBA 有种特殊场景：当前user没有权限（-4），这里校验的时候需要展示报错信息
          if (
            identityType === IDENTITY_TYPE_MAP.TT_USER &&
            availableStatus === IDENTITY_AVAILABLE_STATUS.USER_NO_PRIVILEGE
          ) {
            return callback(
              new ValidationError({
                message: i18n.t('module_creative_identity_not_authorized'),
                messageI18nKey: '',
              }),
            );
          }

          // 其他场景已经有了alert提示的，就不展示报错信息了
          return callback(
            new ValidationError({
              message: ' ',
              messageI18nKey: '',
              // message:
              //   UNAVAILABLE_IDENTITY_ACCOUNT_STATUS_MAP()[identityData?.availableStatus as number]?.editUnAvailableDesc ||
              //   i18n.t('module_creative_identity_select_validate_info'),
              // messageI18nKey: '',
            }),
          );
        }
        if (!identityId) {
          return callback(
            new ValidationError({
              message: i18n.t('module_creative_identity_select_validate_info'),
              messageI18nKey: 'module_creative_identity_select_validate_info',
              moduleName: i18n.t('adgroup_shop_ad_type'),
              subModuleName: i18n.t('module_creative_identity'),
            }),
          );
        }

        return callback();
      },
      trigger: 'change',
    },
  ]);

  return {
    identityValidator,
  };
}

export function useTikTokIdentityDisplayProps(data: {
  identityInfo: Ref<{ identityId: string; identityTtsShopId?: string; identityType: number; identityBcId: string }>;
  identityData?: Ref<IdentityInfo>;
  adgroupData?: Ref<AdgroupFormData>;
  creativeData?: Ref<FullNormalFormData>;
}) {
  const { TT_USER, FAKE_USER } = IDENTITY_TYPE_MAP;
  const { identityAdditionalInfo } = useIdentityAdditional();
  const avatar = ref('');
  const displayName = ref('');
  const canManageVideo = ref(true);
  const canUseVideoList = ref(true);
  const canUseShowcase = ref(true);
  const avatarIcon = ref({});

  const hasIdentityDisplayName = computed(() => {
    const curData = data?.identityData?.value;
    return Boolean(curData?.displayName);
  });

  const setDisplayProps = (info: IdentityInfo) => {
    avatar.value = (info.avatarIcon?.url || '') as string;
    displayName.value = info.displayName;
    canManageVideo.value = info.canManageVideo ?? false;
    canUseVideoList.value = info.canUseVideoList;
    canUseShowcase.value = info.canUseShowcase; // 不设置默认值，undefined 表示为接口还未回来,false/true表示接口回来
    avatarIcon.value = info.avatarIcon;
  };

  const getIdentity = async () => {
    const { attach_uid } = parseUrlSearch(window.location.search);
    const { identityId, identityBcId, identityTtsShopId, identityType } = data.identityInfo.value;
    if (!identityId) {
      // custom identity 为空的场景需要特殊处理
      if (identityType === FAKE_USER && identityAdditionalInfo.value.listLength === 0) {
        setDisplayProps(data?.identityData?.value || {});
      }
      // bindTtba 为空的场景需要特殊处理
      if (identityType === TT_USER) {
        setDisplayProps({});
      }
      return;
    }
    // 避免重复请求
    if (hasIdentityDisplayName.value && !attach_uid) {
      setDisplayProps({
        ...data.identityData.value,
        canUseVideoList: canUseVideoList.value,
        canUseShowcase: canUseShowcase.value,
      });
      return;
    }
    const info = await useIdentityInfoApi({
      identityId,
      identityType,
      identityBcId,
      identityTtsShopId,
      attachUid: attach_uid,
    });
    setDisplayProps(info.identityInfo);
  };

  const getAffiliateIdentity = async () => {
    if (data.adgroupData && data.creativeData) {
      const result = await useAffiliateVideoInfoApi({
        shop_id: data.adgroupData.value.shopId,
        bc_id: data.adgroupData.value.shopAuthorizedBc,
        product_id: data.creativeData.value.spuList?.[0] || '',
        item_id: data.creativeData.value.videoInfo?.awemeItemId || data.creativeData.value.awemeItemId || '',
      });

      if (!result?.ads_affiliate_video) {
        setDisplayProps({});
        return;
      }
      setDisplayProps({
        avatarIcon: result.ads_affiliate_video.avatar_icon,
        canUseShowcase: false,
        canUseVideoList: true,
        canManageVideo: false,
        displayName: result.ads_affiliate_video.uniq_id,
      });
    }
  };

  watch(
    () => data.identityInfo.value.identityId,
    () => {
      if (data.identityInfo.value.identityType === IDENTITY_TYPE_MAP.AFFILIATE) {
        getAffiliateIdentity();
      } else {
        getIdentity();
      }
    },
    {
      immediate: true,
    },
  );

  return {
    avatar,
    displayName,
    canManageVideo,
    canUseVideoList,
    canUseShowcase,
    avatarIcon,
  };
}

export function useNativeTypeSwitchDisabled(opt: {
  createMode: Ref<string>;
  creativeData: Ref<Partial<FullNormalFormData & AcoFormData>>;
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  isDisabledCreative: Ref<boolean>;
}) {
  const { createMode, creativeData, isDisabledCreative, adgroupData, campaignData } = opt;
  const { isEdit } = useCreativeActionTypeIs({
    createMode,
    adgroupFormData: adgroupData,
  });
  const { isAco } = useStdCreativeAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const { isCreateInExistAcoAdgroup } = useCreativeAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
    createMode,
  });
  /** 禁止TTBA与auth 授权TT之间相互切换 */
  const isDisabledSwitchNativeType = computed(() => {
    if (isDisabledCreative.value) {
      return true;
    }
    const isTTBAPushVideoAd =
      creativeData.value?.identityType === IDENTITY_TYPE_MAP.TT_USER &&
      creativeData.value?.nativeAdActionType === NATIVE_AD_ACTION_TYPE.PUSH_VIDEO_TO_TTBA;
    // 编辑ACO 或在已有ACO ad group下新建 不可切换identity类型
    if (isAco.value) {
      return isEdit.value || isCreateInExistAcoAdgroup.value;
    }
    // 非ACO：推视频创意在编辑时不能切换
    if (isEdit.value) {
      return Boolean(isTTBAPushVideoAd);
    }
    return false;
  });
  return {
    isDisabledSwitchNativeType,
  };
}

export function useIdentityAvatarUrl(avatar: AvatarIcon): string {
  if (avatar?.url) {
    return avatar.url;
  }
  if (avatar?.webUri) {
    return getImgSrcByEnv({ URL: imgSrcPrefix.URL }, avatar.webUri);
  }
  return defaultAvatar;
}
